<script setup lang="ts">
    import { inject, ref, watch } from 'vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import EventEditorDetailsNav from '@views/Event/EventEditor/Details/EventEditorDetailsNav.vue';
    import { type Event } from '@views/Event/EventTypes';
    import { eventKey, saveEventPropertyKey, canEditKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;

    const registrationLimit = ref(0);
    const registrationQueueLimit = ref(0);

    watch(
        () => event.value?.RegistrationLimit,
        (value) => (registrationLimit.value = Number(value ?? 0)),
        { immediate: true }
    );
    watch(
        () => event.value?.RegistrationQueueLimit,
        (value) => (registrationQueueLimit.value = Number(value ?? 0)),
        { immediate: true }
    );

    function saveRegistrationLimit(newValue: number) {
        registrationLimit.value = newValue;
        saveEventProperty('RegistrationLimit', String(newValue), $Resources.EventLimitLabel);
        // Without a limit the queue limit is hidden, so clear it whether the limit was toggled off or emptied
        if (newValue === 0 && registrationQueueLimit.value !== 0) saveRegistrationQueueLimit(0);
    }

    function saveRegistrationQueueLimit(newValue: number) {
        registrationQueueLimit.value = newValue;
        saveEventProperty('RegistrationQueueLimit', String(newValue), $Resources.EventQueueLimitLabel);
    }

    function blurValue(e: FocusEvent) {
        return Number((e.target as HTMLInputElement).value);
    }

    function onRegistrationLimitBlur(e: FocusEvent) {
        const newValue = blurValue(e);
        if (newValue !== registrationLimit.value) saveRegistrationLimit(newValue);
    }

    function onRegistrationQueueLimitBlur(e: FocusEvent) {
        const newValue = blurValue(e);
        if (newValue !== registrationQueueLimit.value) saveRegistrationQueueLimit(newValue);
    }

    function toggleRegistrationLimit() {
        saveRegistrationLimit(registrationLimit.value === 0 ? 100 : 0);
    }

    function toggleRegistrationQueueLimit() {
        saveRegistrationQueueLimit(registrationQueueLimit.value === 0 ? 20 : 0);
    }
</script>

<template>
    <div v-if="event" class="max-700">
        <h2 class="h3 m-0-t m-lg-b">{{ $Resources.EventHowManyCanAttendLabel }}?</h2>

        <div class="form-group m-sm-b">
            <FormCheckable
                :label="$Resources.EventSetMaxLimitAttendeeToggleLabel"
                :switch-style="SwitchStyle.Standard"
                :checked="registrationLimit !== 0"
                :disabled="!canEdit"
                @update:modelValue="toggleRegistrationLimit" />
        </div>

        <FormInput
            v-if="registrationLimit !== 0"
            class="form-group"
            style="max-width: 110px"
            type="number"
            :label="$Resources.EventLimitLabel"
            :modelValue="registrationLimit"
            :disabled="!canEdit"
            :min="0"
            @onBlur="onRegistrationLimitBlur" />

        <template v-if="registrationLimit !== 0">
            <div class="form-group m-sm-b">
                <FormCheckable
                    :label="$Resources.EventSetAttendeeQueueLimitToggleLabel"
                    :switch-style="SwitchStyle.Standard"
                    :checked="registrationQueueLimit !== 0"
                    :disabled="!canEdit"
                    @update:modelValue="toggleRegistrationQueueLimit" />
            </div>

            <FormInput
                v-if="registrationQueueLimit !== 0"
                class="form-group"
                style="max-width: 110px"
                type="number"
                :label="$Resources.EventQueueLimitLabel"
                :modelValue="registrationQueueLimit"
                :disabled="!canEdit"
                :min="0"
                @onBlur="onRegistrationQueueLimitBlur" />
        </template>
    </div>

    <EventEditorDetailsNav v-if="event" :eventId="props.eventId" />
</template>
